import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Svg, Path } from '@react-pdf/renderer';

const DARK = '#1E293B';
const GREY = '#475569';
const LINE = '#E2E8F0';

const S = StyleSheet.create({
  page: { padding: 40, backgroundColor: '#FFFFFF', fontFamily: 'Helvetica' },
  
  // Header Section
  headerTopRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 },
  logo: { width: 100 },
  tourNameBlock: { flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
  tourNameRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  tourName: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: DARK },
  
  headerSubRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8 },
  tagline: { fontSize: 9, color: GREY, fontStyle: 'italic' },
  tourNumberDur: { fontSize: 9, color: GREY },
  
  divider: { height: 1, backgroundColor: DARK, width: '100%', marginBottom: 12 },
  
  headerDetailsRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20 },
  leftDetails: { flexDirection: 'column', gap: 4 },
  rightDetails: { flexDirection: 'column', alignItems: 'flex-end', gap: 4 },
  detailText: { fontSize: 10, color: DARK },
  detailLabel: { fontFamily: 'Helvetica-Bold' },

  // Itinerary Section
  dayBlock: { marginBottom: 24 },
  dayHeaderRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#000000', borderRadius: 0, paddingHorizontal: 10, paddingVertical: 6, marginBottom: 12 },
  
  dayBox: { borderRightWidth: 1, borderRightColor: '#333333', paddingRight: 10, marginRight: 10, justifyContent: 'center' },
  dayBoxText: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#FFFFFF' },
  
  dayTitleBlock: { flexDirection: 'column', flex: 1 },
  dayTitle: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#FFFFFF' },
  daySubtitle: { fontSize: 9, color: '#CBD5E1', marginTop: 2, fontStyle: 'italic' },
  
  dayDesc: { fontSize: 10, color: GREY, textAlign: 'justify', lineHeight: 1.6, marginBottom: 12 },
  
  // Experience Block
  expBlock: { flexDirection: 'row', gap: 14, marginBottom: 16, alignItems: 'flex-start' },
  expImg: { width: 210, height: 140, objectFit: 'cover', borderRadius: 4 },
  expContent: { flex: 1, flexDirection: 'column' },
  expName: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: DARK, marginBottom: 4 },
  expDesc: { fontSize: 9, color: GREY, textAlign: 'justify', lineHeight: 1.6 },

  // Inclusions Exclusions
  incExcSection: { marginTop: 20, marginBottom: 20 },
  incExcHeading: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: DARK, marginBottom: 10 },
  incExcRow: { flexDirection: 'row', gap: 20 },
  incExcCol: { flex: 1 },
  incExcTitle: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: DARK, marginBottom: 6 },
  incExcItem: { flexDirection: 'row', gap: 6, marginBottom: 4 },
  bulletInc: { fontSize: 10, color: '#10B981' },
  bulletExc: { fontSize: 10, color: '#EF4444' },
  incExcText: { fontSize: 9, color: GREY, flex: 1, lineHeight: 1.4 },

  // Notice blocks
  noticeBlock: { marginTop: 16, padding: 12, backgroundColor: '#FEF2F2', borderRadius: 4, borderWidth: 1, borderColor: '#FECACA' },
  noticeText: { fontSize: 9, color: '#DC2626', lineHeight: 1.5, fontFamily: 'Helvetica-Bold' },
  infoBlock: { marginTop: 8, padding: 12, backgroundColor: '#F8FAFC', borderRadius: 4, borderWidth: 1, borderColor: '#E2E8F0' },
  infoText: { fontSize: 9, color: '#475569', lineHeight: 1.5, fontStyle: 'italic' },
  
  footer: { position: 'absolute', bottom: 20, left: 40, right: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', borderTopWidth: 1, borderTopColor: LINE, paddingTop: 10 },
  footerCopy: { fontSize: 8, color: GREY },
  footerPage: { fontSize: 8, color: GREY }
});

const padDay = n => `Day ${String(n).padStart(2, '0')}`;

const duration = count => {
  const n = Number(count) || 0;
  if (!n) return '—';
  return `${n} Days / ${Math.max(0, n - 1)} Nights`;
};

const formatDate = d => {
  if (!d) return '—';
  return d.split('T')[0];
};

const resolveImg = img => {
  if (!img) return null;
  let url = typeof img === 'string' ? img : (img.h || img.l || null);
  if (url && url.startsWith('/')) {
    url = 'https://engine.kithtravels.com' + url;
  }
  return url;
};

// SVG Icons for Featured Inclusions
const InclusionIcon = ({ name }) => {
  const n = name.toLowerCase();
  let d = "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"; // default check
  
  if (n.includes('hotel') || n.includes('accommodation') || n.includes('stay')) {
    d = "M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z"; // bed
  } else if (n.includes('transport') || n.includes('vehicle') || n.includes('car')) {
    d = "M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"; // car
  } else if (n.includes('breakfast') || n.includes('food') || n.includes('meal')) {
    d = "M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-7v12h2.5v8H21V2h-5z"; // food
  } else if (n.includes('flight')) {
    d = "M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"; // flight
  } else if (n.includes('ticket') || n.includes('entrance') || n.includes('fee')) {
    d = "M22 10V6c0-1.1-.9-2-2-2H4c-1.1 0-1.99.9-1.99 2v4c1.1 0 1.99.9 1.99 2s-.89 2-2 2v4c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-4c-1.1 0-2-.9-2-2s.9-2 2-2zm-2 7.74c-1.16-.94-1.92-2.33-1.99-3.74h-1v-2h1c.07-1.41.83-2.8 1.99-3.74V6H4v2.26c1.16.94 1.92 2.33 1.99 3.74h1v2H6c-.07 1.41-.83 2.8-1.99 3.74V18h16v-2.26z"; // ticket
  }

  return (
    <Svg viewBox="0 0 24 24" width={12} height={12} style={{ marginLeft: 4 }}>
      <Path d={d} fill={DARK} />
    </Svg>
  );
};


const splitTextForWrap = (text, charLimit = 400) => {
  if (!text) return { part1: '', part2: '' };
  if (text.length <= charLimit) return { part1: text, part2: '' };
  
  // Find the nearest space after the charLimit to avoid splitting words
  let splitIndex = text.indexOf(' ', charLimit);
  if (splitIndex === -1) splitIndex = text.length; // If no space found, take the whole string
  
  return {
    part1: text.substring(0, splitIndex),
    part2: text.substring(splitIndex).trim()
  };
};

function ItineraryPDF({
  tourName = 'Tour Itinerary',
  tourRef = '',
  arrivalDate = '',
  arrivalFlight = '',
  arrivalTime = '',
  departureDate = '',
  departureFlight = '',
  departureTime = '',
  itinerary = [],
  inclusions = [],
  exclusions = [],
  featuredInclusions = [],
  clientName = '',
  logoUrl = 'https://kithtravels.com/kith-logo.png',
}) {
  const totalDays = itinerary.length;
  
  // Clean up tour name
  let displayTourName = tourName;
  if (!displayTourName || displayTourName === 'Tour Itinerary' || displayTourName === 'Custom Tour Plan') {
    displayTourName = `${totalDays} Day Tour in Sri Lanka`;
  }

  // Fallback exclusions if not provided
  const defaultExclusions = [
    'International air tickets',
    'Visa fees',
    'Personal expenses',
    'Special permits (drones, camera, etc.)',
    "Anything haven't mentioned in the itinerary"
  ];
  const finalExclusions = defaultExclusions;

  return (
    <Document title={displayTourName} author="KITH Travels">
      <Page size="A4" style={S.page}>

        {/* HEADER (No 'fixed' prop so it only appears on Page 1) */}
        <View style={S.headerTopRow}>
          <Image src={logoUrl} style={S.logo} />
          <View style={S.tourNameBlock}>
            <View style={S.tourNameRow}>
              <Text style={S.tourName}>{displayTourName}</Text>
              {(featuredInclusions || []).map((inc, i) => (
                <InclusionIcon key={i} name={inc} />
              ))}
            </View>
          </View>
        </View>
        <View style={S.headerSubRow}>
          <Text style={S.tagline}>Discover Sri Lanka, Where Every Journey Tells a Story</Text>
          <Text style={S.tourNumberDur}>{tourRef ? `Ref: ${tourRef} | ` : ''}{duration(totalDays)}</Text>
        </View>
        
        <View style={S.divider} />
        
        <View style={S.headerDetailsRow}>
          <View style={S.leftDetails}>
            <Text style={S.detailText}><Text style={S.detailLabel}>From:</Text> {formatDate(arrivalDate)}</Text>
            <Text style={S.detailText}><Text style={S.detailLabel}>To:</Text> {formatDate(departureDate)}</Text>
          </View>
          <View style={S.rightDetails}>
            {clientName && <Text style={S.detailText}><Text style={S.detailLabel}>Client: </Text><Text style={S.detailLabel}>{clientName}</Text></Text>}
            <Text style={S.detailText}><Text style={S.detailLabel}>Arrival:</Text> {arrivalFlight || 'TBA'}</Text>
            <Text style={S.detailText}><Text style={S.detailLabel}>Departure:</Text> {departureFlight || 'TBA'}</Text>
          </View>
        </View>

        {/* ITINERARY */}
        {itinerary.map((day, idx) => {
          const exps = (day.experiences || []).filter(Boolean);
          return (
            <View key={idx} style={S.dayBlock} wrap={false}>
              <View style={S.dayHeaderRow}>
                <View style={S.dayBox}>
                  <Text style={S.dayBoxText}>{padDay(day.day || idx + 1)}</Text>
                </View>
                <View style={S.dayTitleBlock}>
                  <Text style={S.dayTitle}>{day.title || day.location || `Day ${idx + 1}`}</Text>
                  {day.subtitle ? <Text style={S.daySubtitle}>{day.subtitle}</Text> : null}
                </View>
              </View>
              
              {day.description ? <Text style={S.dayDesc}>{day.description}</Text> : null}

              {exps.map((exp, ei) => {
                const name = typeof exp === 'string' ? exp : (exp.title || exp.name || '');
                const desc = typeof exp === 'object' ? exp.description : '';
                const imgSrc = typeof exp === 'object' ? resolveImg(exp.image) : null;
                
                return (
                  <View key={ei} style={{ marginBottom: 20 }} wrap={false}>
                    {name ? <Text style={[S.expName, { marginBottom: 8, fontSize: 11 }]}>{name}</Text> : null}
                    <View style={{ flexDirection: 'row', gap: 16, alignItems: 'flex-start' }}>
                      {imgSrc ? <Image src={imgSrc} style={{ width: 240, height: 160, objectFit: 'cover', borderRadius: 4 }} /> : null}
                      <View style={{ flex: 1, flexDirection: 'column' }}>
                        {desc ? <Text style={{ fontSize: 9, color: GREY, textAlign: 'justify', lineHeight: 1.6 }}>{desc}</Text> : null}
                      </View>
                    </View>
                  </View>
                );
              })}
            </View>
          );
        })}

        {/* INCLUSIONS & EXCLUSIONS */}
        <View style={S.incExcSection} wrap={false}>
          <Text style={S.incExcHeading}>Inclusions &amp; Exclusions</Text>
          <View style={S.incExcRow}>
            {inclusions.length > 0 && (
              <View style={S.incExcCol}>
                <Text style={S.incExcTitle}>✓ What's Included</Text>
                {inclusions.map((item, i) => (
                  <View key={i} style={S.incExcItem}>
                    <Text style={S.bulletInc}>•</Text>
                    <Text style={S.incExcText}>{item}</Text>
                  </View>
                ))}
              </View>
            )}
            <View style={S.incExcCol}>
              <Text style={S.incExcTitle}>✗ Not Included</Text>
              {finalExclusions.map((item, i) => (
                <View key={i} style={S.incExcItem}>
                  <Text style={S.bulletExc}>•</Text>
                  <Text style={S.incExcText}>{item}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {/* NOTICES */}
        <View style={S.noticeBlock} wrap={false}>
          <Text style={S.noticeText}>
            We strongly recommend arriving at the airport a minimum of 3 hours prior to your scheduled departure time to avoid any unforeseen delays or complications at immigration.
          </Text>
        </View>
        <View style={S.infoBlock} wrap={false}>
          <Text style={S.infoText}>
            This is a system-generated itinerary. Please contact your dedicated KITH Travels tour coordinator for any clarifications or amendments before travel.
          </Text>
        </View>

        {/* FOOTER */}
        <View style={S.footer} fixed>
          <Text style={S.footerCopy}>© 2026 KITH Travels (Pvt) Ltd. All rights reserved. | kithtravels.com</Text>
          <Text style={S.footerPage} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}

export default ItineraryPDF;
